import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Plus, Minus } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'How fast can I go live with Sport Stream Kit?',
      answer: 'Typically under 5 minutes. Create a session, set teams/lineups, open the Output URL in your streaming software, and control everything from the web Control Panel.'
    },
    {
      question: 'Does it work with OBS/Streamlabs?',
      answer: 'Yes. Add the Output URL as a Browser Source (1920×1080 recommended) in OBS, Streamlabs, or similar tools. No local installs needed—the graphics render in your browser.'
    },
    {
      question: 'Which sports are supported?',
      answer: 'Football is first‑class (scoreboard, clock, lineups, subs, events, stats). The architecture is multi‑sport and we’re adding more sports over time.'
    },
    {
      question: 'Can I match the graphics to my brand?',
      answer: 'Absolutely. Use skins to set colors, fonts, and accents. You can upload crests/logos and configure panels (e.g., Lineups, Match Info, Substitutions) to fit your branding.'
    },
    {
      question: 'How do I control the match clock and events?',
      answer: 'From the Control Panel: start/pause the clock, set the current period (1H, 2H, ET, pens), trigger lineups, goals, cards, subs, and show/hide overlays in real time.'
    },
    {
      question: 'Can I manage squads and lineups easily?',
      answer: 'Yes. Create teams and players, import players via CSV, set default jersey numbers, and reorder match lineups with drag‑and‑drop. Substitutions and events update overlays instantly.'
    },
    {
      question: 'Are there plan limits?',
      answer: 'Yes—limits like matches/week, max teams/competitions/players, and custom skins depend on Starter, Pro, or Elite. Limits are enforced automatically. See the Pricing page for exact numbers.'
    },
    {
      question: 'Do I need special hardware?',
      answer: 'No. Any modern computer with a recent Chrome/Edge/Firefox works. You’ll need streaming software (e.g., OBS) and a stable internet connection.'
    },
    {
      question: 'How is pricing handled?',
      answer: 'Plans are sold as an annual license (one‑off payment per year). You can upgrade anytime; renewals are optional and can be managed before the next cycle.'
    },
    {
      question: 'What support is included?',
      answer: 'Email support for all plans, priority responses for Pro, and concierge help for Elite. We’re also building out docs and short how‑to videos.'
    },
    {
      question: 'Can multiple people collaborate?',
      answer: 'Yes. Create sessions and control them from the web. Output can be embedded in your streaming scene while another person operates the Control Panel.'
    },
    {
      question: 'Does it work with my existing data sources?',
      answer: 'You can run fully manual from the Control Panel today. API/data integrations are on the roadmap; contact us if you need a specific provider.'
    }
  ];


  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 relative">
      <div className="absolute inset-0 bg-slate-900"></div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Frequently Asked
            <span className="block bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Questions
            </span>
          </h2>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Get answers to common questions about Sport Stream Kit and how it can transform your sports broadcasting.
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden hover:border-slate-600/50 transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.05 }}
              viewport={{ once: true }}
            >
              <button
                className="w-full p-6 text-left flex items-center justify-between hover:bg-slate-700/30 transition-colors duration-200"
                onClick={() => toggleFAQ(index)}
              >
                <h3 className="text-lg font-semibold text-white pr-4">
                  {faq.question}
                </h3>
                <motion.div
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex-shrink-0"
                >
                  {openIndex === index ? (
                    <Minus className="h-5 w-5 text-blue-400" />
                  ) : (
                    <Plus className="h-5 w-5 text-slate-400" />
                  )}
                </motion.div>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6">
                      <div className="h-px bg-slate-700 mb-4"></div>
                      <p className="text-slate-300 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Contact CTA */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="bg-gradient-to-r from-blue-600/20 to-cyan-500/20 backdrop-blur-sm border border-blue-500/30 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-4">
              Still have questions?
            </h3>
            <p className="text-slate-300 mb-6">
              Our team is here to help you get started with Sport Stream Kit
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-cyan-600 transition-all duration-200"
              onClick={() => window.open('mailto:info@sportstreamkit.com')}>
                Contact Support
              </button>
              <button className="bg-slate-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-slate-600 transition-colors"
                onClick={() => window.open('mailto:info@sportstreamkit.com')}>
                Schedule Demo
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;